import React, { useState } from "react"
import { useMutation } from "urql"

import { sendAmplitudeData } from "../../support/amplitude-react"
import { inviteClientsInBatches, getInviteImportBatchCount } from "../../utils/inviteClientsInBatches"
import ClientImportProgress from "../shared/ClientImportProgress"
import LoadingSpinner from "../shared/LoadingSpinner"

import ChooseMethod from "./ChooseMethod"
import Confirmation from "./Confirmation"
import CopyPaste from "./CopyPaste"
import { parseInviteClientRows } from "./parseInviteClientImport"
import Review from "./Review"

const InviteClientsMutation = `
  mutation InviteClients($clients: [ClientInput!]!) {
    inviteClients (clients: $clients) {
      erroredClients {
        email
      }
    }
  }
`

export default function InviteClients() {
  const [active, setActive] = useState("file")
  const [step, setStep] = useState(1)
  const [clients, setClients] = useState([])
  const [existingClients, setExistingClients] = useState([])
  const [field, setField] = useState("")
  const [, inviteClients] = useMutation(InviteClientsMutation)
  const [errorMessage, setErrorMessage] = useState("")
  const [importSubmitError, setImportSubmitError] = useState("")
  const [importProgress, setImportProgress] = useState(null)

  const handleChoiceUpdate = (newVal) => {
    setActive(newVal)
  }

  const handleChoice = (choice) => {
    if (choice === "file") {
      setStep(2)
      sendAmplitudeData("import method selected", { "Method Selected": "CSV" })
    } else if (choice === "single") {
      window.location.href = "/portal/clients?show_add_client_form=1"
    } else {
      setStep(3)
      sendAmplitudeData("import method selected", { "Method Selected": "Copy Paste" })
    }
  }

  const handleFieldChange = (val) => {
    sendAmplitudeData("import row edited")
    setField(val)
  }

  const handleFileUpload = () => {
    setStep(4)
    sendAmplitudeData("csv uploaded")
  }

  const review = (nextClients) => {
    setImportSubmitError("")
    setClients(nextClients)
    setStep(4)
  }

  const editContacts = () => {
    setStep(3)
  }

  const submitContacts = () => {
    const totalRows = clients.length
    const batchCount = getInviteImportBatchCount(totalRows)
    setImportProgress(totalRows > 0 ? { completedBatches: 0, batchCount, processedRows: 0, totalRows } : null)
    setStep(6)
    ;(async () => {
      try {
        const { erroredClients, lastError } = await inviteClientsInBatches(
          clients,
          (vars) => inviteClients(vars),
          setImportProgress
        )

        if (lastError) {
          console.error(lastError) // eslint-disable-line no-console
          setImportSubmitError(
            `Import stopped before every row was processed. Some clients may already be in your directory—refresh the list before retrying. Details: ${lastError}`
          )
          setStep(4)
          return
        }

        sendAmplitudeData("import clients submitted", {
          "Total Rows": clients.length,
          "Errored Imports": erroredClients.length
        })
        setExistingClients(erroredClients)
        setStep(5)
      } finally {
        setImportProgress(null)
      }
    })()
  }

  const reviewInput = (userInput, invitationType) => {
    const clients = parseInviteClientRows(userInput, invitationType)
    if (clients.length) {
      if (invitationType === "csv_upload" || invitationType === "csv_import") {
        sendAmplitudeData("csv upload sucess", { "Valid Rows": clients.length })
      } else {
        sendAmplitudeData("copy paste sucess", { "Valid Rows": clients.length })
      }

      review(clients)
    } else {
      if (invitationType === "csv_upload" || invitationType === "csv_import") {
        sendAmplitudeData("csv upload failed")
      } else {
        sendAmplitudeData("copy paste failed")
      }

      setErrorMessage(
        "Please make sure there is at least one valid email or phone. Columns can be either: Email, First name, Last name, Phone, Notes — or: First name, Last name, Email, Phone, Notes. Include a header row if you like. Standard CSV with quoted fields is supported."
      )
    }
    handleFieldChange(userInput)
  }

  const SwitchCase = ({ value }) => {
    switch (value) {
      case 1:
        return (
          <ChooseMethod
            handleChoice={handleChoice}
            active={active}
            handleChoiceUpdate={handleChoiceUpdate}
            handleFileUpload={handleFileUpload}
            reviewInput={reviewInput}
          />
        )
      case 2:
        return <div />
      case 3:
        return <CopyPaste field={field} reviewInput={reviewInput} errorMessage={errorMessage} />
      case 4:
        return (
          <Review
            clients={clients}
            submitContacts={submitContacts}
            editContacts={editContacts}
            submitError={importSubmitError}
          />
        )
      case 5:
        return <Confirmation clients={clients} existingClients={existingClients} />
      case 6:
        return (
          <div className="columns is-centered">
            <div className="column is-three-fifths">
              {importProgress && importProgress.totalRows > 0 ? (
                <div className="box" style={{ padding: "8px 16px 24px" }}>
                  <ClientImportProgress {...importProgress} />
                </div>
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
        )
    }
  }

  return (
    <div>
      <SwitchCase value={step} />
    </div>
  )
}
